import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Plus, MapPin, Users, Search, Map as MapIcon, Image as ImageIcon, Trash2, Save, AlertTriangle } from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";
import Loader from "@/components/Loader";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const newId = () => Math.random().toString(36).slice(2, 10);
const fileToData = (file, cb) => { if (!file) return; const r = new FileReader(); r.onload = () => cb(r.result); r.readAsDataURL(file); };

export default function Lieux() {
  const location = useLocation();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", capacity: "", contact_id: "", contact_name: "", contact_phone: "", notes: "" });
  const [selected, setSelected] = useState(null);
  const [contacts, setContacts] = useState([]);

  const load = () => api.get("/venues").then(({ data }) => setVenues(data)).finally(() => setLoading(false));
  useEffect(() => { load(); api.get("/contacts").then(({ data }) => setContacts(data)).catch(() => {}); }, []);
  useEffect(() => {
    const openId = new URLSearchParams(location.search).get("open");
    if (openId) openVenue(openId);
  }, [location.search]); // eslint-disable-line

  const create = async () => {
    if (!form.name) return toast.error("Le nom du lieu est requis");
    try {
      await api.post("/venues", { ...form, capacity: form.capacity ? parseInt(form.capacity) : null, plans: [], photos: [] });
      toast.success("Lieu créé");
      setOpen(false);
      setForm({ name: "", address: "", capacity: "", contact_id: "", contact_name: "", contact_phone: "", notes: "" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
  };

  const openVenue = async (id) => { const { data } = await api.get(`/venues/${id}`); setSelected(data); };

  const filtered = venues.filter((v) => v.name.toLowerCase().includes(q.toLowerCase()) || (v.address || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="venues-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-white sm:text-4xl">Lieux</h1>
          <p className="mt-1 text-sm text-zinc-500">{venues.length} lieu(x) récurrent(s) — plans et positions matériel.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-venue-btn" className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Plus className="mr-1 h-4 w-4" />Lieu</Button>
          </DialogTrigger>
          <DialogContent className="border-zinc-800 bg-zinc-950">
            <DialogHeader><DialogTitle className="text-white">Nouveau lieu</DialogTitle><DialogDescription className="text-zinc-400">Enregistrez un lieu récurrent avec ses plans et photos.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-zinc-400">Nom *</Label><Input data-testid="venue-name" className="mt-1 border-zinc-800 bg-zinc-900" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Domaine des Oliviers…" /></div>
              <div><Label className="text-zinc-400">Adresse</Label><AddressAutocomplete testid="venue-address" className="mt-1 border-zinc-800 bg-zinc-900" value={form.address} onChange={(v) => setForm((f) => ({ ...f, address: v }))} onSelect={(r) => setForm((f) => ({ ...f, address_coords: r.coordinates || null }))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-zinc-400">Capacité (pers.)</Label><Input data-testid="venue-capacity" type="number" className="mt-1 border-zinc-800 bg-zinc-900" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></div>
                <div><Label className="text-zinc-400">Contact sur place</Label><Input className="mt-1 border-zinc-800 bg-zinc-900" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
              </div>
              <div><Label className="text-zinc-400">Téléphone contact</Label><Input className="mt-1 border-zinc-800 bg-zinc-900" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="06 12 34 56 78" /></div>
              <div>
                <Label className="text-zinc-400">Contact lié (salle / fournisseur)</Label>
                <select data-testid="venue-contact-select" value={form.contact_id || ""} onChange={(e) => setForm({ ...form, contact_id: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white">
                  <option value="">— Aucun —</option>
                  {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div><Label className="text-zinc-400">Notes (accès, parking, restrictions…)</Label><Textarea className="mt-1 border-zinc-800 bg-zinc-900" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <Button data-testid="save-venue-btn" onClick={create} className="w-full rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]">Enregistrer</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
        <Input data-testid="venue-search" placeholder="Rechercher un lieu…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-full border-zinc-800 bg-zinc-900 pl-10" />
      </div>

      {loading ? (
        <Loader label="Chargement des lieux…" />
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 bg-zinc-900/40 py-16 text-center" data-testid="venues-empty">
          <MapPin className="mx-auto h-12 w-12 text-zinc-700" />
          <p className="mt-4 font-medium text-white">Aucun lieu enregistré</p>
          <p className="mt-1 text-sm text-zinc-500">Ajoutez les lieux récurrents où vous intervenez, avec leurs plans et positions matériel.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((v) => (
            <button key={v.id} data-testid={`venue-card-${v.id}`} onClick={() => openVenue(v.id)}
              className="rounded-2xl border border-zinc-800 bg-zinc-900 p-4 text-left transition-colors hover:border-[#FF6A00]/50 hover:bg-zinc-900/60">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#FF6A00]/30 to-[#FFD500]/20 text-white"><MapPin className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-white">{v.name}</p>
                  <p className="truncate text-xs text-zinc-500">{v.address || "Adresse non renseignée"}</p>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-zinc-400">
                {v.capacity ? <span className="flex items-center gap-1 rounded-full bg-zinc-800 px-2 py-0.5"><Users className="h-3 w-3" />{v.capacity} pers.</span> : null}
                <span className="flex items-center gap-1 rounded-full bg-zinc-800 px-2 py-0.5"><MapIcon className="h-3 w-3" />{(v.plans || []).length} plan(s)</span>
                <span className="flex items-center gap-1 rounded-full bg-zinc-800 px-2 py-0.5"><ImageIcon className="h-3 w-3" />{(v.photos || []).length} photo(s)</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <VenueSheet venue={selected} contacts={contacts} onClose={() => setSelected(null)} onSaved={(v) => { setSelected(v); load(); }} onDeleted={() => { setSelected(null); load(); }} />
    </div>
  );
}

function VenueSheet({ venue, contacts = [], onClose, onSaved, onDeleted }) {
  const [draft, setDraft] = useState(null);
  const [savedSnap, setSavedSnap] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delOpen, setDelOpen] = useState(false);

  const payload = (d) => ({
    name: d.name, contact_id: d.contact_id || null, address: d.address || "", address_coords: d.address_coords || null,
    capacity: d.capacity || null, contact_name: d.contact_name || "", contact_phone: d.contact_phone || "",
    notes: d.notes || "", plans: d.plans || [], photos: d.photos || [],
  });

  useEffect(() => {
    if (venue) {
      const d = { ...venue, plans: venue.plans || [], photos: venue.photos || [] };
      setDraft(d);
      setSavedSnap(JSON.stringify(payload(d)));
    } else { setDraft(null); setSavedSnap(null); }
  }, [venue]);

  const dirty = !!draft && savedSnap !== null && JSON.stringify(payload(draft)) !== savedSnap;
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  if (!draft) return <Sheet open={false}><SheetContent /></Sheet>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/venues/${draft.id}`, payload(draft));
      const { data } = await api.get(`/venues/${draft.id}`);
      const d = { ...data, plans: data.plans || [], photos: data.photos || [] };
      setDraft(d);
      setSavedSnap(JSON.stringify(payload(d)));
      onSaved(data);
      toast.success("Lieu enregistré");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur d'enregistrement"); }
    finally { setSaving(false); }
  };

  const addFiles = (kind, files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    let pending = arr.length;
    const added = [];
    arr.forEach((file) => fileToData(file, (url) => {
      added.push({ id: newId(), label: file.name.replace(/\.[^.]+$/, ""), url });
      if (--pending === 0) setDraft((d) => ({ ...d, [kind]: [...(d[kind] || []), ...added] }));
    }));
  };
  const removeItem = (kind, id) => setDraft((d) => ({ ...d, [kind]: (d[kind] || []).filter((x) => x.id !== id) }));
  const relabel = (kind, id, label) => setDraft((d) => ({ ...d, [kind]: d[kind].map((x) => (x.id === id ? { ...x, label } : x)) }));

  const del = async () => {
    try { await api.delete(`/venues/${draft.id}`); toast.success("Lieu supprimé"); setDelOpen(false); onDeleted(); }
    catch { toast.error("Erreur de suppression"); }
  };

  const closeGuard = (o) => {
    if (o) return;
    if (dirty && !window.confirm("Vous avez des modifications non enregistrées. Quitter sans enregistrer ?")) return;
    onClose();
  };

  return (
    <Sheet open={!!venue} onOpenChange={closeGuard}>
      <SheetContent className="w-full overflow-y-auto border-zinc-800 bg-zinc-950 text-white sm:max-w-lg" data-testid="venue-sheet">
        <SheetHeader><SheetTitle className="text-white">{draft.name}</SheetTitle></SheetHeader>
        {dirty && (
          <div data-testid="venue-unsaved-banner" className="sticky top-0 z-30 mt-3 flex items-center justify-between gap-3 rounded-xl border border-[#FFD500]/40 bg-[#1a1710] px-4 py-2.5 text-sm text-[#FFD500] shadow-lg">
            <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 shrink-0" />Modifications non enregistrées</span>
            <Button data-testid="venue-unsaved-save" onClick={save} disabled={saving} className="h-8 shrink-0 rounded-full bg-[#FF6A00] px-4 text-xs font-semibold text-white hover:bg-[#E65F00]"><Save className="mr-1 h-3.5 w-3.5" />Enregistrer</Button>
          </div>
        )}
        <div className="mt-5 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-zinc-400">Nom</Label><Input data-testid="venue-sheet-name" className="mt-1 border-zinc-800 bg-zinc-900" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></div>
            <div><Label className="text-xs text-zinc-400">Capacité</Label><Input type="number" className="mt-1 border-zinc-800 bg-zinc-900" value={draft.capacity || ""} onChange={(e) => setDraft({ ...draft, capacity: e.target.value ? parseInt(e.target.value) : null })} /></div>
          </div>
          <div><Label className="text-xs text-zinc-400">Adresse</Label><Input className="mt-1 border-zinc-800 bg-zinc-900" value={draft.address || ""} onChange={(e) => setDraft({ ...draft, address: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-zinc-400">Contact sur place</Label><Input className="mt-1 border-zinc-800 bg-zinc-900" value={draft.contact_name || ""} onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })} /></div>
            <div><Label className="text-xs text-zinc-400">Téléphone contact</Label><Input className="mt-1 border-zinc-800 bg-zinc-900" value={draft.contact_phone || ""} onChange={(e) => setDraft({ ...draft, contact_phone: e.target.value })} placeholder="06 12 34 56 78" /></div>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Contact lié (salle / fournisseur)</Label>
            <select data-testid="venue-sheet-contact" value={draft.contact_id || ""} onChange={(e) => setDraft({ ...draft, contact_id: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white">
              <option value="">— Aucun —</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {draft.contact && <p className="mt-1 text-xs text-zinc-500" data-testid="venue-contact-info">{[draft.contact.email, draft.contact.phone].filter(Boolean).join(" · ") || "Contact lié"}</p>}
          </div>
          <div><Label className="text-xs text-zinc-400">Notes</Label><Textarea rows={3} className="mt-1 border-zinc-800 bg-zinc-900" value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></div>

          <MediaSection kind="plans" title="Plans du lieu" icon={MapIcon} items={draft.plans} onAdd={(f) => addFiles("plans", f)} onRemove={(id) => removeItem("plans", id)} onLabel={(id, l) => relabel("plans", id, l)} testid="plans" />
          <MediaSection kind="photos" title="Photos — positions matériel" icon={ImageIcon} items={draft.photos} onAdd={(f) => addFiles("photos", f)} onRemove={(id) => removeItem("photos", id)} onLabel={(id, l) => relabel("photos", id, l)} testid="photos" />

          {(draft.events || []).length > 0 && (
            <div data-testid="venue-history">
              <p className="mb-2 text-sm font-semibold text-white">Historique — événements à ce lieu</p>
              <div className="space-y-1.5">
                {draft.events.map((ev) => (
                  <div key={ev.id} className="flex items-center justify-between rounded-lg border border-zinc-800 p-2 text-sm">
                    <span className="truncate text-zinc-300">{ev.title}</span>
                    <span className="shrink-0 text-xs text-zinc-500">{ev.date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <Button data-testid="venue-save" onClick={save} disabled={saving || !dirty} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00] disabled:opacity-50"><Save className="mr-1.5 h-4 w-4" />Enregistrer</Button>
            <button data-testid="venue-delete" onClick={() => setDelOpen(true)} className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" />Supprimer ce lieu</button>
          </div>
        </div>
        <ConfirmDialog
          open={delOpen} onOpenChange={setDelOpen}
          title="Supprimer ce lieu ?"
          description={`« ${draft.name} » sera définitivement supprimé, ainsi que ses plans et photos. Cette action est irréversible.`}
          onConfirm={del} testid="del-venue-dialog"
        />
      </SheetContent>
    </Sheet>
  );
}

function MediaSection({ title, icon: Icon, items, onAdd, onRemove, onLabel, testid }) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <p className="flex items-center gap-2 text-sm font-semibold text-white"><Icon className="h-4 w-4 text-[#FF6A00]" />{title}</p>
        <label className="cursor-pointer rounded-full bg-zinc-800 px-3 py-1 text-xs text-zinc-200 hover:bg-zinc-700">
          + Ajouter
          <input type="file" accept="image/*" multiple className="hidden" data-testid={`venue-${testid}-input`} onChange={(e) => { onAdd(e.target.files); e.target.value = ""; }} />
        </label>
      </div>
      {(items || []).length === 0 ? (
        <p className="rounded-xl border border-dashed border-zinc-800 p-4 text-center text-xs text-zinc-500">Aucun élément — ajoutez des images.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3" data-testid={`venue-${testid}-grid`}>
          {items.map((it) => (
            <div key={it.id} className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900">
              <img src={it.url} alt={it.label} className="h-28 w-full object-cover" />
              <div className="flex items-center gap-1 p-2">
                <Input value={it.label} onChange={(e) => onLabel(it.id, e.target.value)} className="h-7 border-zinc-800 bg-zinc-950 text-xs" />
                <button onClick={() => onRemove(it.id)} className="shrink-0 text-zinc-500 hover:text-red-400" data-testid={`venue-${testid}-remove-${it.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
